import React, { useState, useMemo, useEffect, useRef } from 'react'
import StaticGoogleMap from 'app/maps/StaticGoogleMap'
import { useClient } from 'jsonapi-react'
import mapIcon from 'assets/svg/map-icon.svg'
import clockIcon from 'assets/img/item-clock.png'
import { format, set as setTime, addDays } from 'date-fns'
import styles from './EventMap.module.css'
import GoogleMap from 'app/maps/GoogleMap'
import Marker from './EventMapMarker'

export default function EventMap(props) {
  const {
    lat,
    lng,
    center,
    boundingBox,
    timezone,
    zoom,
  } = props

  const client = useClient()
  const [data, setData] = useState([])
  const mapRef = useRef()
  const [loaded, setLoaded] = useState(false)
  const [bounds, setBounds] = useState()
  const [activeMarker, setActiveMarker] = useState(null)
  const periods = useMemo(() => getPeriods(timezone), [])

  const [activePeriod, setActivePeriod] = useState(() => {
    return periods.find(p => p.active) || periods[0]
  })

  useEffect(() => {
    if (!bounds) {
      return
    }

    const calEventPromise = client.fetch(['calendar_events', {
      expand_recurring: true,
      filter: {
        event_id: 'null',
        lapsed: false,
        bounding_box: bounds,
        min_date: format(activePeriod.min_date, 'yyyy-MM-dd HH:mm:ss'),
        max_date: format(activePeriod.max_date, 'yyyy-MM-dd HH:mm:ss'),
      },
      include: [
        'caterer.photo',
      ],
      page: {
        size: 100,
      },
    }], {
      ssr: false
    })

    const regEventPromise = client.fetch(['events', {
      filter: {
        private: false,
        bounding_box: bounds,
        start_date: {
          gte: format(activePeriod.min_date, 'yyyy-MM-dd'),
          lte: format(activePeriod.max_date, 'yyyy-MM-dd HH:mm:ss')
        },
      },
      include: [
        'occasion',
        'photo',
      ],
      page: {
        size: 100,
      },
    }], {
      ssr: false
    })

    Promise.all([
      calEventPromise,
      regEventPromise,
    ]).then(([calEvents, regEvents]) => {
      let data = []

      if (calEvents.data) {
        data = data.concat(calEvents.data.map(attrs => ({ ...attrs, _type: 'calendar_events' })))
      }

      if (regEvents.data) {
        data = data.concat(regEvents.data.map(attrs => ({ ...attrs, _type: 'events' })))
      }

      setData(data)
    })
  }, [bounds, activePeriod])

  const events = useMemo(() => {
    if (!activePeriod || !activePeriod.min_time) {
      return data
    }
    return data.filter(event => (
      event.address?.location &&
      event.start_date <= activePeriod.max_time &&
      event.end_date >= activePeriod.min_time
    ))
  }, [data, activePeriod])

  const markers = useMemo(() => {
    if (!events) {
      return []
    }
    return events.map(event => ({
      key: `${event._type}_${event.id}`,
      event,
      isActive: event === activeMarker,
      setIsActive: active => setActiveMarker(active ? event : null),
    }))
  }, [events, activeMarker])

  return (
    <div
      className={[
        styles.container
      ]}
    >
      {loaded ? (
        <>
          <GoogleMap
            zoom={zoom}
            center={{ lat, lng }}
            onLoad={map => {
              mapRef.current = map
            }}
            onIdle={() => {
              if (bounds || !mapRef.current) {
                return
              }

              let coords = mapRef.current.getBounds()

              if (coords) {
                coords = coords.toJSON()
                setBounds({
                  max_x: coords.east,
                  max_y: coords.north,
                  min_x: coords.west,
                  min_y: coords.south,
                })
              } else {
                setBounds(boundingBox)
              }
            }}
            onClick={() => {
              if (activeMarker) {
                setActiveMarker(null)
              }
            }}
            markers={markers.map(marker => (
              <Marker {...marker} />
            ))}
          />
          <div
            className={[
              'absolute flex bg-white leading-none uppercase text-xs text-center',
              'top-0 left-0 right-0 sm:right-auto mt-1 ml-1 mr-1 sm:mt-2 sm:ml-2 sm:mr-0',
            ]}
          >
            <div className="bg-navy p-2 hidden sm:block">
              <img src={clockIcon} className="h-10" />
            </div>
            {periods.map(period => (
              <div
                key={period.title}
                onClick={() => {
                  setActivePeriod(period)
                }}
                className={[
                  'p-2 sm:p-3 flex-grow',
                  activePeriod === period ? 'bg-red text-white' : 'text-navy',
                  activePeriod !== period && 'cursor-pointer hover:bg-gray-200',
                  period.key === 'tomorrow' && 'hidden sm:block',
                ]}
              >
                <div className="mb-1 font-bold">
                  {period.title}
                </div>
                <div className={['tracking-wide font-light', activePeriod !== period && 'text-gray-900']}>
                  {period.range}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div
          onClick={() => {
            setLoaded(true)
          }}
          className={[
            'absolute inset-0 flex items-center justify-center bg-gray-300',
          ]}
        >
          <StaticGoogleMap
            lat={lat}
            lng={lng}
            center={center}
            className="absolute inset-0"
          />
          <div
            className="absolute inset-0 bg-black opacity-25"
          />
          <button className="flex items-center absolute btn btn-blue py-4 px-5 shadow">
            <img
              src={mapIcon}
              className="block mr-3"
              width="24"
              alt="Food Truck Map"
            />
            <span className="text-xl font-platform text-white leading-none">
              See Food Trucks Near Me
            </span>
          </button>
        </div>
      )}
    </div>
  )
}

function getPeriods(timeZone) {
  const now = timeZone
    ? new Date(new Date().toLocaleString('en-US', { timeZone }))
    : new Date()

  const today = setTime(now, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
  const tomorrow = addDays(today, 1)

  const todayEnd = setTime(tomorrow, { hours: 3 })
  const tomorrowEnd = addDays(tomorrow, 1)

  return [
    {
      title: 'Breakfast',
      range: '5-11AM',
      min_date: today,
      max_date: todayEnd,
      min_time: setTime(today, { hours: 5 }),
      max_time: setTime(today, { hours: 11 }),
    },
    {
      title: 'Lunch',
      range: '11AM-4PM',
      min_date: today,
      max_date: todayEnd,
      min_time: setTime(today, { hours: 11 }),
      max_time: setTime(today, { hours: 16 }),
    },
    {
      title: 'Dinner',
      range: '4PM-9PM',
      min_date: today,
      max_date: todayEnd,
      min_time: setTime(today, { hours: 16 }),
      max_time: setTime(today, { hours: 21 }),
    },
    {
      title: 'Late Night',
      range: '9PM-3AM',
      min_date: today,
      max_date: todayEnd,
      min_time: setTime(today, { hours: 21 }),
      max_time: todayEnd,
    },
    {
      key: 'tomorrow',
      title: 'Tomorrow',
      min_date: tomorrow,
      max_date: tomorrowEnd,
    },
  ].map(p => ({
    ...p,
    active: p.max_time > now
  }))
}
